/**
 * VistoeraTASK-00626 — one-shot seed: Text Assets "加入画布" → new blank
 * Content Project Canvas opens with a single text_asset node.
 *
 * Mirrors `imageAssetJoinCanvasSeed` (TASK-00625): sessionStorage, keyed to
 * the NEW projectId, consumed once after Canvas hydrates. Content is a
 * snapshot — editing the Canvas node does not mutate the library asset
 * (critical for team-shared text).
 */

export const JOIN_TEXT_ASSET_SEED_KEY = 'vistoera.canvas.joinTextAssetSeed.v1'

export type JoinTextAssetSeed = {
  workspaceId: string
  projectId: string
  /** Text Asset Center presentation id (e.g. workspace_text:… / canvas_node:…). */
  presentationId: string
  title: string
  /** Full plain-text body snapshot for fields.text. */
  text: string
  assetType?: string
  libraryCanonicalSourceType?: string
  libraryCanonicalSourceId?: string
  nonce: string
}

function readRaw(): JoinTextAssetSeed | null {
  if (typeof sessionStorage === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(JOIN_TEXT_ASSET_SEED_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<JoinTextAssetSeed>
    if (
      !parsed ||
      typeof parsed.workspaceId !== 'string' ||
      typeof parsed.projectId !== 'string' ||
      typeof parsed.presentationId !== 'string' ||
      typeof parsed.text !== 'string' ||
      typeof parsed.nonce !== 'string'
    ) {
      return null
    }
    return {
      workspaceId: parsed.workspaceId,
      projectId: parsed.projectId,
      presentationId: parsed.presentationId,
      title: typeof parsed.title === 'string' ? parsed.title : '',
      text: parsed.text,
      assetType: typeof parsed.assetType === 'string' ? parsed.assetType : undefined,
      libraryCanonicalSourceType:
        typeof parsed.libraryCanonicalSourceType === 'string'
          ? parsed.libraryCanonicalSourceType
          : undefined,
      libraryCanonicalSourceId:
        typeof parsed.libraryCanonicalSourceId === 'string'
          ? parsed.libraryCanonicalSourceId
          : undefined,
      nonce: parsed.nonce,
    }
  } catch {
    return null
  }
}

export function writeJoinTextAssetSeed(seed: JoinTextAssetSeed): void {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.setItem(JOIN_TEXT_ASSET_SEED_KEY, JSON.stringify(seed))
  } catch {
    /* ignore quota / private mode */
  }
}

/** Returns the seed only when it matches this workspace+project, then clears it. */
export function consumeJoinTextAssetSeed(
  workspaceId: string,
  projectId: string,
): JoinTextAssetSeed | null {
  const seed = readRaw()
  if (!seed) return null
  if (seed.workspaceId !== workspaceId || seed.projectId !== projectId) return null
  clearJoinTextAssetSeed()
  return seed
}

export function clearJoinTextAssetSeed(): void {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.removeItem(JOIN_TEXT_ASSET_SEED_KEY)
  } catch {
    /* ignore */
  }
}

export function makeJoinTextAssetSeedNonce(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `join-text-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}
